import { useEffect } from "react"
import { useParams, useOutletContext, useNavigate } from "react-router-dom"
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react"
import _ from "lodash-es"

import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'

import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon"
import ConfirmationModal from "../../components/ConfirmationModal"
import PageTitle from "../../components/PageTitle.jsx"
import TimestampWithUTCTooltip from "../../components/TimestampWithUTCTooltip"
import UploadFormModal from "./components/UploadFormModal"

import { NotificationTypes, addNotification } from "../../stores/notification-store.js"
import { DownloadDeviceForm, DeleteDeviceForm, fetchDeviceForms } from '../../utils/api'

import useDeviceForms from "../../hooks/useDeviceForms.js"
import { ComponentDeviceType } from "../../utils/constants"

function Forms() {

	const navigate = useNavigate()
	const { beId } = useParams()
	//const [searchParams] = useSearchParams()
	//const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData  } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewForms) {
            addNotification('You are not authorized to view UTG Forms for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value])
    
    const canEditSignal = useComputed(() => {
        return userPermissionsSummary?.value?.canEditForms
    })

	const { deviceFormsSignal, deviceSchemasSignal } = useDeviceForms(beId, config, accessToken)

	const selectedFormSignal = useSignal(null)

	const currentPageSignal = useSignal(1)
	const filterStringSignal = useSignal("")

	const rowsPerPageSignal = useSignal(parseInt(window.localStorage.getItem('pagesize')) || 250)
	const selectedRowsSignal = useSignal([])
	const showModalUploadFormSignal = useSignal(false)
	const showModalDeleteFormSignal = useSignal(false)
	const sortColumnSignal = useSignal('')

	const showDownloadProgressSignal = useSignal(false)
	const downloadProgressMessageSignal = useSignal('')

	const SortDirectionType = {
		ASCENDING: 1,
		DESCENDING: 2
	}
	const sortDirectionSignal = useSignal(SortDirectionType.DESCENDING)
    
    useSignalEffect(() => {
        window.localStorage.setItem('pagesize', rowsPerPageSignal.value)
    })

	const filteredDataSignal = useComputed(() => {
		try {
			if (!filterStringSignal.value) {
				return deviceFormsSignal.value ? [...deviceFormsSignal.value] : []
			}

			// Convert the filter to lowercase
			const lowerCaseFilter = filterStringSignal.value.toLowerCase()

			// Filter the data based on the filter text
			return deviceFormsSignal.value.filter(item => {
				// Limit search to these fields
				let searchData = {
					name: item.name,
					deviceType:item.deviceType
				}

				return Object.values(searchData).some(value => {
					if (typeof value != 'undefined') {
						return value.toString().toLowerCase().includes(lowerCaseFilter)
					}
				})
			})
		} catch (e) {
			console.error("Forms.filteredData Exception: " + e.toString())
			window.alert("Forms.filteredData Exception: " + e.toString())
			return []
		}
	})

	const sortedDataSignal = useComputed(() => {

		// just return filtered data if no sorting
		if (!sortColumnSignal.value.length) {
			return [...filteredDataSignal.value]
		}

		let _sortedData = [...filteredDataSignal.value]
		return _sortedData.sort((a, b) => {
			let _a = _.get(a, sortColumnSignal.value, null)
			let _b = _.get(b, sortColumnSignal.value, null)

            // Convert nulls to empty strings for sorting purposes
            _a = _a ? _a : ''
            _b = _b ? _b : ''

            // Convert nulls to empty strings for sorting purposes
            _a = _a ? _a : ''
            _b = _b ? _b : ''

            // convert values to lowercase
            if (_a && _a.length) {
                _a = _a.toLowerCase()
            }
            if (_b && _b.length) {
                _b = _b.toLowerCase()
            }

			if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
				if (_a < _b) return -1
				if (_a > _b) return 1
				return 0
			}
			else {
				if (_a > _b) return -1
				if (_a < _b) return 1
				return 0
			}
		})
	})

	const totalPagesSignal = useComputed(() => {
		return Math.ceil(filteredDataSignal.value.length / parseInt(rowsPerPageSignal.value, 10))
	})

	const displayDataSignal = useComputed(() => {
		// Calculate the starting and ending indexes for pagination
		const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
		const endIndex = startIndex + rowsPerPageSignal.value

		return sortedDataSignal.value.slice(startIndex, endIndex)
	})
	const startDisplayRowNumberSignal = useComputed(() => {
		return ((currentPageSignal.value - 1) * rowsPerPageSignal.value) + 1
	})
	const endDisplayRowNumberSignal = useComputed(() => {
		const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
		const endIndex = startIndex + rowsPerPageSignal.value
		return endIndex < filteredDataSignal.value.length ? endIndex : filteredDataSignal.value.length
	})

	const showUploadFormModal = () => showModalUploadFormSignal.value = true
	const handleCloseModalUploadForm = () => showModalUploadFormSignal.value = false

	const handleFilterStringChange = (e) => {
		currentPageSignal.value = 1
		filterStringSignal.value = e.currentTarget.value
	}

	const handleHeaderTableClick = datafield => {
		// if already sorted on this column then toggle sort direction
		if (datafield == sortColumnSignal.value) {
			sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
			return
		}

		// update sort column - the computed functions will then run and render the sorted data
		sortColumnSignal.value = datafield
	}

	const accordionData = [
		{ name: 'Properties', data: { Count: deviceFormsSignal.value.length, 'Filter Count': filteredDataSignal.value.length, 'Display Count': displayDataSignal.value.length, Filter: filterStringSignal.value, CurrentPage: currentPageSignal.value, RowsPerPage: rowsPerPageSignal.value, accessToken: accessToken } },
		{ name: 'Filtered Data', data: filteredDataSignal.value },
		{ name: 'Data', data: deviceFormsSignal.value },
		{ name: 'Display Data', data: displayDataSignal.value },
		{ name: 'Selected Rows', data: selectedRowsSignal.value },
		{ name: 'Access Token', data: accessToken },
		{ name: 'App Config', data: config }
	]

	const handlePageSizeSelect = (v) => {
		currentPageSignal.value = 1
		rowsPerPageSignal.value = parseInt(v, 10)
	}

	const invertedDeviceTypesSignal = useComputed(() => {
		return Object.keys(ComponentDeviceType).reduce((accumulator, key) => {
			accumulator[ComponentDeviceType[key]] = key.toLowerCase()
			return accumulator;
		}, {})
	})

	const humanReadableDeviceTypesSignal = useComputed(() => {
		if (deviceSchemasSignal.value.length == 0) {
			return []
		}
		const lowerCaseDeviceTypes = {}
		const keys = Object.keys(ComponentDeviceType)
		for (let i = 0; i < keys.length; i++) {
			const key = keys[i]
			const lowerKey = key.toLowerCase()
			lowerCaseDeviceTypes[lowerKey] = ComponentDeviceType[key]
		}
		let device_types_to_names = []
		const schemas = deviceSchemasSignal.value
		for (let i = 0; i < schemas.length; i++) {
			const schema = schemas[i]
			device_types_to_names[lowerCaseDeviceTypes[schema.key]] = schema.name
		}
		//console.log("Returning the following:")
		//console.log(device_types_to_names)
		return device_types_to_names
	})

	function PaginationItems() {
		let items = []
		for (let i = 1; i <= totalPagesSignal.value; i++) {
			items.push(
				<Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => currentPageSignal.value = i}>{i}</Pagination.Item>
			)
		}
		return items
	}

	function TableColumnHeader({ title, sortable, datafield }) {
		// non-sortable columns do not have click handlers
		if (!sortable) {
			return (
				<th>{title}</th>
			)
		}

		// sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
		if (datafield != sortColumnSignal.value) {
			return (
				<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
			)
		}

		// datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
		if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
			return (
				<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
					<Icon icon="caret-down" width="16" height="16"></Icon>
				</th>
			)
		}

		// else must be ascending
		return (
			<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
				<Icon icon="caret-up" width="16" height="16"></Icon>
			</th>
		)
	}

	const showDeleteModal = function (item) {
		selectedFormSignal.value = item
		showModalDeleteFormSignal.value = true
	}

	const handleCloseModalDeleteForm = function () {
		selectedFormSignal.value = null
		showModalDeleteFormSignal.value = false
	}

	const downloadForm = async function (item) {
		downloadProgressMessageSignal.value = 'Downloading...'
		showDownloadProgressSignal.value = true
		let success_callback = () => { showDownloadProgressSignal.value = false }
		await DownloadDeviceForm(beId, item, config, accessToken, success_callback)
	}

	const deleteFormMessageSignal = useComputed(() => {
		if (selectedFormSignal.value != null) {
			return `Are you sure you want to delete this form: ${selectedFormSignal.value.name}`
		}
		return null
	})

	const deleteForm = async function (record) {
		if (record) {
			downloadProgressMessageSignal.value = "Deleting form..."
			showDownloadProgressSignal.value = true
			const success_callback = async function(){
				showDownloadProgressSignal.value = false
				deviceFormsSignal.value = await fetchDeviceForms(beId, config, accessToken)
				addNotification(`Form ${record.name} was deleted successfully.`, NotificationTypes.SUCCESS, false, 'Form deleted')
			}
			const error_callback = (exception) => {
				showDownloadProgressSignal.value = false
				addNotification(`Deletion of form ${record.name} failed: `.concat(exception.toString()), NotificationTypes.ERROR, false, 'Form deletion failed')
			}
			DeleteDeviceForm(beId, record, config, accessToken, success_callback, error_callback)
		}
		handleCloseModalDeleteForm()
	}

	function TableRow({ item }) {
		if (!Object.prototype.hasOwnProperty.call(item, "humanReadableDeviceType")) {
			const deviceNames = humanReadableDeviceTypesSignal.value
			const length = deviceNames.length
			//console.log("There are ".concat(length).concat(" device types"))
			//console.log(deviceNames)
			if (item.deviceType >= length) {
				item.humanReadableDeviceType = "Unknown device type ".concat(item.deviceType)
			}
			else {
				item.humanReadableDeviceType = deviceNames[item.deviceType]
			}
		}
		return (
			<tr>
				<td>
					<Dropdown>
						<Dropdown.Toggle variant="link" size="sm" id="actions"><Icon icon="gear" width="16" height="16"></Icon></Dropdown.Toggle>
						<Dropdown.Menu>
							<Dropdown.Item onClick={() => downloadForm(item)}><Icon icon="download" width="16" height="16"></Icon>&nbsp;Download form</Dropdown.Item>
							{canEditSignal.value && <Dropdown.Item onClick={() => showDeleteModal(item)}>
								<span style={{ color: "#f00" }}>
									<Icon icon="trash" width="16" height="16"></Icon>
								</span>
								&nbsp;Delete form
							</Dropdown.Item>}
						</Dropdown.Menu>
					</Dropdown>
				</td>
				<td>{item.name}</td>
				<td data-devicecode={ item.deviceType }>{item.humanReadableDeviceType}</td>
				<td><TimestampWithUTCTooltip date={item.lastUpdatedAt}></TimestampWithUTCTooltip></td>
			</tr>
		)
	}

	function TableRows({ items }) {
		try {
			if (items && items.length) {
				const rows = items.map(item => {
					return <TableRow item={item} key={`${item.id}`} />
				})

				return (
					<tbody>{rows}</tbody>
				)
			}
			else if (items) {
				return (
					<tbody><tr><td colSpan="18" className="text-center">No device forms found</td></tr></tbody>
				)
			}
		} catch (e) {
			console.error("Forms.TableRows Exception: " + e.toString())
			window.alert("Forms.TableRows Exception: " + e.toString())
			return <div style={{ color: '#f00' }}>Error</div>
		}
	}

	function DownloadProgress() {
		if (showDownloadProgressSignal.value) {
			return (
				<div className="d-flex flex-column align-items-center justify-content-center">
					<Spinner className="mt-5" animation="border" role="status">
						<span className="visually-hidden">{downloadProgressMessageSignal.value}</span>
					</Spinner>
					<p className="mt-3">{downloadProgressMessageSignal.value}</p>
				</div>
			)
		}
	}

	return (
		<>
			<DownloadProgress></DownloadProgress>
			<ConfirmationModal show={showModalDeleteFormSignal.value} handleCancel={handleCloseModalDeleteForm} handleConfirm={() => { showModalDeleteFormSignal.value = false; deleteForm(selectedFormSignal.value); }} message={deleteFormMessageSignal.value} title={"Delete Form"} />
            <PageTitle title='Form Manager' canEditSignal={canEditSignal} />
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <DropdownButton variant="outline-secondary" className="me-auto" title={`${rowsPerPageSignal} records per page`} id="pageSize-dropdown" onSelect={handlePageSizeSelect}>
                    <Dropdown.Item eventKey="10">10</Dropdown.Item>
                    <Dropdown.Item eventKey="25">25</Dropdown.Item>
                    <Dropdown.Item eventKey="50">50</Dropdown.Item>
                    <Dropdown.Item eventKey="250">250</Dropdown.Item>
                    <Dropdown.Item eventKey="500">500</Dropdown.Item>
                    <Dropdown.Item eventKey="1000">1000</Dropdown.Item>
                </DropdownButton>
                <Nav.Item className="mt-1 me-2">
                    <Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" onChange={handleFilterStringChange} />
                </Nav.Item>
                {canEditSignal.value && <Nav.Item className="mt-1">
					<Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={() => showModalUploadFormSignal.value = true}>
						<span className="me-1">
							<Icon icon='upload' width="16" height="16" ></Icon>
						</span>
						Upload
					</Button>
                </Nav.Item>}
            </Nav>
			<Table responsive striped hover size="sm" className="cc-table">
				<thead>
					<tr>
						<TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
						<TableColumnHeader title={'Name'} sortable={true} datafield={'name'}></TableColumnHeader>
						<TableColumnHeader title={'Device Type'} sortable={true} datafield={'humanReadableDeviceType'}></TableColumnHeader>
						<TableColumnHeader title={'Upload Date'} sortable={true} datafield={'lastUpdatedAt'}></TableColumnHeader>
					</tr>
				</thead>
				<TableRows items={displayDataSignal.value} />
				<tfoot><tr><td /*className="fs-6"*/ colSpan={19}>Displaying {startDisplayRowNumberSignal.value}-{endDisplayRowNumberSignal.value} of {filteredDataSignal.value.length}</td></tr></tfoot>
			</Table>
			<Pagination /*className="px-3 d-flex flex-wrap"*/><PaginationItems /></Pagination>
			<DebugAccordion data={accordionData} debug={true} />

			<UploadFormModal
				show={showModalUploadFormSignal.value}
				handleCancel={handleCloseModalUploadForm}
				deviceTypes={deviceSchemasSignal.value}
				rerenderPage={async function (){ deviceFormsSignal.value = await fetchDeviceForms(beId, config, accessToken) } }
			></UploadFormModal>
		</>
	)
}

export default Forms